import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import {
  FileText,
  Plus,
  Trash2,
  Calendar,
  User,
  Shield,
  CheckSquare,
  Lock,
  X,
} from "lucide-react";
import AutoGrowTextarea from "./Autogrow";
import { format, addDays } from "date-fns";
import { showToast } from "../utils/toast";
import { handleAPIError } from "../utils/errorHandler";
import { actionPointAPI, reportAPI } from "../Services/apiService";
import UserCombobox from "./UserCombobox";
import UserMultiSelect from "./UserMultiSelect";
import { ConfirmDialog, useConfirmDialog } from "./ui/confirm-dialog";
import { truncateText } from "../lib/utils";
import ResponsiveTooltip from "./ui/ResponsiveTooltip";
import { Download, Upload } from "lucide-react";
import * as XLSX from "xlsx";

const MOMModal = ({ open, onClose, meeting, users = [], onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [existingActionPoints, setExistingActionPoints] = useState([]);
  const [newActionPoints, setNewActionPoints] = useState([]);
  const fileInputRef = React.useRef(null);

  // Ref for table auto-scroll
  const tableContainerRef = useRef(null);
  const prevAPLength = useRef(0);
  const apIdCounter = useRef(0);

  // Auto-scroll to bottom when a new row is added
  useEffect(() => {
    if (newActionPoints.length > prevAPLength.current) {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTo({
          top: tableContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }
    prevAPLength.current = newActionPoints.length;
  }, [newActionPoints.length]);

  // Confirmation dialogs
  const deleteConfirm = useConfirmDialog();
  const closeConfirm = useConfirmDialog();
  const [itemToDelete, setItemToDelete] = useState(null);

  const [isDescDirty, setIsDescDirty] = useState(false);
  const descCancelConfirm = useConfirmDialog();

  const [descModalOpen, setDescModalOpen] = useState(false);
  const [activeAP, setActiveAP] = useState(null);
  const [tempDescription, setTempDescription] = useState("");

  // Check if form has unsaved changes
  const hasUnsavedChanges = () => {
    return newActionPoints.some(
      (ap) =>
        ap.description.trim() !== "" ||
        (ap.assignee_ids && ap.assignee_ids.length > 0) ||
        ap.approver_id,
    );
  };

  const openDescriptionModal = (ap) => {
    setActiveAP(ap);
    setTempDescription(ap.description || "");
    setDescModalOpen(true);
  };

  // Handle modal close with confirmation
  const handleModalClose = (openState) => {
    if (!openState && hasUnsavedChanges()) {
      closeConfirm.showConfirm(() => {
        onClose(false);
      });
    } else {
      onClose(openState);
    }
  };

  // Filter users: only active users, vendors can be assignees but NOT approvers
  const assigneeOptions = useMemo(() => {
    return users.filter((u) => {
      const isActive = u.is_active === true || u.is_active === undefined;
      const notDeleted = !u.is_deleted;
      return isActive && notDeleted;
    });
  }, [users]);

  const approverOptions = useMemo(() => {
    return users.filter((u) => {
      const isActive = u.is_active === true || u.is_active === undefined;
      const notDeleted = !u.is_deleted;
      const notVendor = u.role !== "vendor";
      return isActive && notDeleted && notVendor;
    });
  }, [users]);

  // Fetch existing action points when modal opens
  useEffect(() => {
    const fetchExistingActionPoints = async () => {
      if (open && meeting?.id) {
        setIsLoadingExisting(true);
        try {
          const response = await actionPointAPI.getActionPointsForMeeting(
            meeting.id,
            {
              occurrence_date: meeting.scheduled_date,
            },
          );
          const existing = response.data || [];
          setExistingActionPoints(existing);
        } catch (error) {
          console.error("Failed to fetch existing action points:", error);
          setExistingActionPoints([]);
        } finally {
          setIsLoadingExisting(false);
        }
      }
    };

    fetchExistingActionPoints();
  }, [open, meeting?.id]);

  const createEmptyActionPoint = () => ({
    id: ++apIdCounter.current,
    type: "action",
    description: "",
    assignee_ids: [],
    approver_id: "",
    due_date: format(addDays(new Date(), 7), "yyyy-MM-dd"),
    document_required: false,
  });

  // Initialize with one empty action point when modal opens
  useEffect(() => {
    if (open) {
      setNewActionPoints([createEmptyActionPoint()]);
    } else {
      setNewActionPoints([]);
    }
  }, [open]);

  const handleAddRow = () => {
    setNewActionPoints((prev) => [...prev, createEmptyActionPoint()]);
  };

  const handleDeleteRow = (id) => {
    if (newActionPoints.length === 1) {
      showToast.error("At least one action point is required");
      return;
    }

    // Find the action point being deleted
    const apToDelete = newActionPoints.find((ap) => ap.id === id);
    setItemToDelete(apToDelete);

    // Show confirmation dialog
    deleteConfirm.showConfirm(() => {
      setNewActionPoints((prev) => prev.filter((ap) => ap.id !== id));
      showToast.success("Action point removed");
    });
  };

  const handleFieldChange = (id, field, value) => {
    setNewActionPoints((prev) =>
      prev.map((ap) => (ap.id === id ? { ...ap, [field]: value } : ap)),
    );
  };

  const validateActionPoints = () => {
    const errors = [];

    newActionPoints.forEach((ap, index) => {
      if (!ap.description?.trim()) {
        errors.push(`Row ${index + 1}: Description is required`);
      }
      // Only validate assignees, approver, and due date if type is 'action'
      if (ap.type === "action") {
        if (!ap.assignee_ids || ap.assignee_ids.length === 0) {
          errors.push(`Row ${index + 1}: At least one assignee is required`);
        }
        // Approver is now optional - only validate conflict if approver is selected
        if (
          ap.approver_id &&
          ap.assignee_ids &&
          ap.assignee_ids.includes(parseInt(ap.approver_id))
        ) {
          errors.push(
            `Row ${index + 1}: Approver cannot be one of the assignees`,
          );
        }
        if (!ap.due_date) {
          errors.push(`Row ${index + 1}: Due date is required`);
        } else {
          const dueDate = new Date(ap.due_date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (dueDate < today) {
            errors.push(`Row ${index + 1}: Due date cannot be in the past`);
          }
        }
      }
    });

    return errors;
  };

  const handleDownloadTemplate = () => {
    const data = [
      { Type: "action", Description: "Complete the quarterly report" },
      { Type: "information", Description: "Budget was approved for Q2" },
    ];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "Action_Points_Template.csv");
    showToast.success("Template downloaded");
  };

  const generateErrorReport = (errors, originalFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split("\n");
      const headers = lines[0].trim().split(",");

      const errorsByRow = {};
      errors.forEach((err) => {
        if (!errorsByRow[err.row]) errorsByRow[err.row] = [];
        errorsByRow[err.row].push(`${err.field}: ${err.message}`);
      });

      const errorData = lines
        .slice(1)
        .map((line, idx) => {
          const rowNum = idx + 2;
          if (errorsByRow[rowNum]) {
            return `${line.trim()},"${errorsByRow[rowNum].join(" | ")}"`;
          }
          return null;
        })
        .filter(Boolean);

      if (errorData.length > 0) {
        const output = [headers.join(",") + ",Error", ...errorData].join("\n");
        const blob = new Blob([output], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Import_Errors_${format(
          new Date(),
          "yyyyMMdd_HHmm",
        )}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      }
    };
    reader.readAsText(originalFile);
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      showToast.error("Please upload a CSV file");
      return;
    }

    setIsLoading(true);
    try {
      const response = await actionPointAPI.validateBulkActionPoints(file);
      const result = response.data;

      if (result.failed > 0) {
        showToast.error(
          `Import failed with ${result.failed} error(s). Error report downloaded.`,
        );
        generateErrorReport(result.errors, file);
      }

      if (result.items && result.items.length > 0) {
        const newRows = result.items.map((item) => ({
          ...createEmptyActionPoint(),
          description: item.description,
          type: item.point_type, // Map point_type to type for MOMModal
        }));

        setNewActionPoints((prev) => {
          if (prev.length === 1 && !prev[0].description) {
            return newRows;
          }
          return [...prev, ...newRows];
        });

        showToast.success(`Imported ${result.items.length} row(s)`);
      }
    } catch (error) {
      handleAPIError(error, "Failed to process CSV file");
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (newActionPoints.length === 0) {
      showToast.error("Please add at least one action point");
      return;
    }

    const errors = validateActionPoints();
    if (errors.length > 0) {
      showToast.error(errors[0]); // Show first error
      return;
    }

    setIsLoading(true);
    try {
      const payload = newActionPoints.map((ap) => ({
        point_type: ap.type || "action", // Backend expects 'point_type' not 'type'
        description: ap.description.trim(),
        assignee_ids: ap.type === "action" ? ap.assignee_ids : null, // Changed to assignee_ids (array)
        approver_id:
          ap.type === "action" && ap.approver_id
            ? parseInt(ap.approver_id)
            : null, // Optional approver
        due_date: ap.type === "action" ? ap.due_date : null,
        document_required: ap.type === "action" ? ap.document_required : false,
      }));

      await actionPointAPI.createActionPoints(meeting.id, payload);
      showToast.success(
        `${newActionPoints.length} action point(s) added successfully`,
      );

      if (onSuccess) {
        onSuccess();
      }

      // Trigger refresh
      window.dispatchEvent(new CustomEvent("actionPointsAdded"));

      onClose();
    } catch (error) {
      handleAPIError(error, "Failed to add action points", true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleModalClose}>
        <DialogContent className="sm:max-w-7xl  w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="sticky top-0 z-10 bg-white border-b px-6 py-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Minutes of Meeting - Add Action Points
                </DialogTitle>
                <DialogDescription className="">
                  {/* add tooltip to the meeting title */}
                  {meeting?.title && (
                    <ResponsiveTooltip
                      content={
                        <p className="text-sm whitespace-pre-wrap">
                          {meeting.title}
                        </p>
                      }
                      className="max-w-md p-3"
                    >
                      <span className="text-sm font-medium cursor-pointer">
                        Meeting: {truncateText(meeting.title, 30)} |
                        {` ${meeting.scheduled_date
                          ? format(
                            new Date(meeting.scheduled_date),
                            "MMM dd, yyyy",
                          )
                          : ""
                          }`}
                      </span>
                    </ResponsiveTooltip>
                  )}
                </DialogDescription>
              </div>
              <DialogClose className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </DialogHeader>

          <div className="flex-1  overflow-y-auto px-6 py-2 space-y-6">
            {/* Existing Action Points (Read-only) */}
            {isLoadingExisting ? (
              <div className="text-center py-2 text-sm text-gray-500">
                Loading existing action points...
              </div>
            ) : (
              existingActionPoints.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Existing Action Points ({existingActionPoints.length}) -
                    Read Only
                  </h4>
                  <div className="bg-gray-50 border rounded-lg p-2 space-y-2 max-h-20 overflow-y-auto">
                    {existingActionPoints.map((ap, idx) => (
                      <div
                        key={ap.id}
                        className="text-sm flex items-center gap-2 pb-2 border-b border-gray-200 last:border-0"
                      >
                        <span className="font-medium text-gray-600 shrink-0">
                          {idx + 1}.
                        </span>
                        <div className="flex items-center space-y-1">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                            {/* <p className="font-medium text-gray-900  ">{truncateText(ap.description, 50)} </p> */}
                            <ResponsiveTooltip
                              content={
                                <p className="text-sm whitespace-pre-wrap">
                                  {ap.description}
                                </p>
                              }
                              className="max-w-md p-3"
                            >
                              <span className="text-sm font-medium cursor-pointer">
                                {truncateText(ap.description, 50)}
                              </span>
                            </ResponsiveTooltip>

                            <span className="flex ml-2 items-center gap-1">
                              <User className="h-3 w-3 text-green-600" />
                              <span className="text-green-600">
                                {ap.assignees && ap.assignees.length > 0
                                  ? ap.assignees
                                    .map((a) => a.full_name)
                                    .join(", ")
                                  : ap.assignee?.full_name || "N/A"}
                              </span>
                            </span>
                            <span className="flex items-center gap-1">
                              <Shield className="h-3 w-3 text-red-600" />
                              <span className="text-red-600">
                                {ap.approver?.full_name || "N/A"}
                              </span>
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3 text-yellow-600" />
                              <span className="text-yellow-600">
                                {ap.due_date
                                  ? format(
                                    new Date(ap.due_date),
                                    "MMM dd, yyyy",
                                  )
                                  : "N/A"}
                              </span>
                            </span>
                            {ap.document_required && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <FileText className="h-3 w-3" />
                                Doc Required
                              </span>
                            )}
                            <Badge variant="secondary" className="text-xs">
                              {ap.status === "pending"
                                ? "Pending"
                                : ap.status === "completed" ||
                                  ap.status === "approved"
                                  ? "Completed"
                                  : ap.status === "submitted_for_review"
                                    ? "In Review"
                                    : ap.status === "rejected_in_review"
                                      ? "Rejected"
                                      : ap.status
                                        ? ap.status
                                          .replace(/_/g, " ")
                                          .split(" ")
                                          .map(
                                            (word) =>
                                              word.charAt(0).toUpperCase() +
                                              word.slice(1).toLowerCase(),
                                          )
                                          .join(" ")
                                        : ap.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}

            {/* New Action Points Table - Desktop */}
            <div className="hidden   md:block sticky bottom-0 z-10">
              <div
                ref={tableContainerRef}
                className="border max-h-[36vh] overflow-y-auto   rounded-lg "
              >
                <Table className="">
                  <TableHeader>
                    <TableRow className="bg-gray-50 sticky top-0 z-10">
                      <TableHead className="w-12 text-center">Sr.</TableHead>
                      <TableHead className="min-w-[140px]">Type</TableHead>
                      <TableHead className="min-w-[250px]">
                        Description <span className="text-red-500">*</span>
                      </TableHead>
                      <TableHead className="min-w-[200px]">
                        Assignees <span className="text-red-500">*</span>
                      </TableHead>
                      <TableHead className="min-w-[160px]">Approver</TableHead>
                      <TableHead className="w-40">
                        Due Date <span className="text-red-500">*</span>
                      </TableHead>
                      <TableHead className="w-28 text-center">
                        Documentary Proof
                      </TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {newActionPoints.map((ap, index) => {
                      const isInformation = ap.type === "information";
                      return (
                        <TableRow key={ap.id}>
                          <TableCell className="text-center font-medium text-gray-600">
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={ap.type || "action"}
                              onValueChange={(value) => {
                                handleFieldChange(ap.id, "type", value);
                                // Clear fields when switching to information
                                if (value === "information") {
                                  handleFieldChange(ap.id, "assignee_ids", []);
                                  handleFieldChange(ap.id, "approver_id", "");
                                  handleFieldChange(
                                    ap.id,
                                    "document_required",
                                    false,
                                  );
                                }
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="action">Action</SelectItem>
                                <SelectItem value="information">
                                  Information
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <div
                              onClick={() => openDescriptionModal(ap)}
                              className="
    cursor-pointer rounded-md border px-3 py-2 text-sm
    hover:bg-muted
    truncate w-[300px]
  "
                              title="Click to edit description"
                            >
                              {ap.description || (
                                <span className="text-gray-400">
                                  Click to add description
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="">
                            <UserMultiSelect
                              users={assigneeOptions}
                              value={ap.assignee_ids || []}
                              onChange={(value) =>
                                handleFieldChange(ap.id, "assignee_ids", value)
                              }
                              placeholder="Select assignees..."
                              className="w-full"
                              disabled={isInformation}
                              enableBackendSearch={true}
                              filterOptions={{ include_vendors: true }}
                              dropdownWidth="min(20vw, 28rem)"
                            />
                          </TableCell>
                          <TableCell>
                            <UserCombobox
                              users={approverOptions}
                              value={
                                ap.approver_id ? String(ap.approver_id) : ""
                              }
                              onChange={(value) =>
                                handleFieldChange(ap.id, "approver_id", value)
                              }
                              placeholder="Search "
                              className="w-full"
                              disabled={isInformation}
                              enableBackendSearch={true}
                              filterOptions={{ include_vendors: true }}
                              dropdownWidth="min(90vw, 250px)"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="date"
                              value={ap.due_date}
                              onChange={(e) =>
                                handleFieldChange(
                                  ap.id,
                                  "due_date",
                                  e.target.value,
                                )
                              }
                              min={format(new Date(), "yyyy-MM-dd")}
                              className="w-full"
                              disabled={isInformation}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox
                              checked={ap.document_required}
                              onCheckedChange={(checked) =>
                                handleFieldChange(
                                  ap.id,
                                  "document_required",
                                  checked,
                                )
                              }
                              disabled={isInformation}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteRow(ap.id)}
                              className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600"
                              disabled={newActionPoints.length === 1}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden  space-y-4">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add New Action Points
              </h4>

              {newActionPoints.map((ap, index) => {
                const isInformation = ap.type === "information";
                return (
                  <Card key={ap.id} className="border-2">
                    <CardContent className="p-4 space-y-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold text-gray-700">
                          Action Point #{index + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRow(ap.id)}
                          className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600"
                          disabled={newActionPoints.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Type</label>
                        <Select
                          value={ap.type || "action"}
                          onValueChange={(value) => {
                            handleFieldChange(ap.id, "type", value);
                            // Clear fields when switching to information
                            if (value === "information") {
                              handleFieldChange(ap.id, "assignee_ids", []);
                              handleFieldChange(ap.id, "approver_id", "");
                              handleFieldChange(
                                ap.id,
                                "document_required",
                                false,
                              );
                            }
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="action">Action</SelectItem>
                            <SelectItem value="information">
                              Information
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <Input
                          placeholder="Enter task description..."
                          value={ap.description}
                          onChange={(e) =>
                            handleFieldChange(
                              ap.id,
                              "description",
                              e.target.value,
                            )
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <label
                          className={`text-sm font-medium flex items-center gap-2 ${isInformation ? "text-gray-400" : ""
                            }`}
                        >
                          <User className="h-4 w-4" />
                          Assignees{" "}
                          <span className="text-red-500">
                            {!isInformation ? "*" : ""}
                          </span>
                        </label>
                        <UserMultiSelect
                          users={assigneeOptions}
                          value={ap.assignee_ids || []}
                          onChange={(value) =>
                            handleFieldChange(ap.id, "assignee_ids", value)
                          }
                          placeholder="Select assignees..."
                          icon={User}
                          disabled={isInformation}
                          enableBackendSearch={true}
                          filterOptions={{ include_vendors: true }}
                        />
                      </div>

                      <div className="space-y-2">
                        <label
                          className={`text-sm font-medium flex items-center gap-2 ${isInformation ? "text-gray-400" : ""
                            }`}
                        >
                          <Shield className="h-4 w-4" />
                          Approver{" "}
                          {!isInformation && (
                            <span className="text-gray-400 text-xs">
                              (Optional)
                            </span>
                          )}
                        </label>
                        <UserCombobox
                          users={approverOptions}
                          value={ap.approver_id ? String(ap.approver_id) : ""}
                          onChange={(value) =>
                            handleFieldChange(ap.id, "approver_id", value)
                          }
                          placeholder="Select approver"
                          // icon={Shield}
                          disabled={isInformation}
                          enableBackendSearch={true}
                          filterOptions={{ include_vendors: false }}
                        />
                      </div>

                      <div className="space-y-2">
                        <label
                          className={`text-sm font-medium flex items-center gap-2 ${isInformation ? "text-gray-400" : ""
                            }`}
                        >
                          <Calendar className="h-4 w-4" />
                          Due Date{" "}
                          <span className="text-red-500">
                            {!isInformation ? "*" : ""}
                          </span>
                        </label>
                        <Input
                          type="date"
                          value={ap.due_date}
                          onChange={(e) =>
                            handleFieldChange(ap.id, "due_date", e.target.value)
                          }
                          min={format(new Date(), "yyyy-MM-dd")}
                          disabled={isInformation}
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`doc-mobile-${ap.id}`}
                          checked={ap.document_required}
                          onCheckedChange={(checked) =>
                            handleFieldChange(
                              ap.id,
                              "document_required",
                              checked,
                            )
                          }
                          disabled={isInformation}
                        />
                        <label
                          htmlFor={`doc-mobile-${ap.id}`}
                          className={`text-sm font-medium cursor-pointer ${isInformation
                            ? "text-gray-400 cursor-not-allowed"
                            : ""
                            }`}
                        >
                          Document Required
                        </label>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              <Dialog
                open={descModalOpen}
                onOpenChange={(open) => {
                  if (!open && isDescDirty) {
                    descCancelConfirm.showConfirm(() => {
                      setDescModalOpen(false);
                      setIsDescDirty(false);
                    });
                  } else {
                    setDescModalOpen(open);
                    setIsDescDirty(false);
                  }
                }}
              >
                <DialogContent
                  className="sm:max-w-xl w-[95vw]"
                  onPointerDownOutside={(e) => e.preventDefault()}
                  onEscapeKeyDown={(e) => e.preventDefault()}
                >
                  <DialogHeader>
                    <DialogTitle>Edit Description</DialogTitle>
                  </DialogHeader>

                  <AutoGrowTextarea
                    value={tempDescription}
                    onChange={(e) => {
                      setTempDescription(e.target.value);
                      setIsDescDirty(true);
                    }}
                    placeholder="Write full description here..."
                  />

                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (isDescDirty) {
                          descCancelConfirm.showConfirm(() => {
                            setDescModalOpen(false);
                            setIsDescDirty(false);
                          });
                        } else {
                          setDescModalOpen(false);
                        }
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      onClick={() => {
                        handleFieldChange(
                          activeAP.id,
                          "description",
                          tempDescription,
                        );
                        setIsDescDirty(false);
                        setDescModalOpen(false);
                      }}
                    >
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Add More and Import Buttons - Mobile */}
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddRow}
                  className="flex items-center gap-2 w-full"
                >
                  <Plus className="h-4 w-4" />
                  Add Row
                </Button>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                    className="flex-1 flex items-center justify-center gap-2 border-dashed border-blue-300"
                  >
                    <Upload className="h-4 w-4 text-blue-600" />
                    Import CSV
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDownloadTemplate}
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Template
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleAddRow}
                className="flex items-center gap-2 text-yellow-700 border-yellow-300 hover:bg-yellow-50"
              >
                <Plus className="h-4 w-4" />
                Add Row
              </Button>

              <div className="flex items-center gap-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleCSVImport}
                  accept=".csv"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading}
                  className="flex items-center gap-1.5 text-blue-600 hover:bg-blue-50"
                >
                  <Upload className="h-3.5 w-3.5" />
                  Import CSV
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-1.5 text-gray-500 hover:text-blue-600"
                >
                  <Download className="h-3.5 w-3.5" />
                  Template
                </Button>
              </div>
            </div>

            <div className="flex gap-3 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isLoading || newActionPoints.length === 0}
                className="bg-yellow-600 hover:bg-yellow-700 flex-1 sm:flex-none"
              >
                {isLoading
                  ? "Adding..."
                  : `Add ${newActionPoints.length} New Action Point${newActionPoints.length > 1 ? "s" : ""
                  }`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={descCancelConfirm.isOpen}
        onOpenChange={descCancelConfirm.setIsOpen}
        onConfirm={descCancelConfirm.handleConfirm}
        title="Discard Description Changes?"
        description="You have unsaved changes in the description. Are you sure you want to cancel?"
        confirmText="Yes, Discard"
        cancelText="Keep Editing"
        variant="warning"
        icon="warning"
      />

      {/* Delete Action Point Confirmation */}
      <ConfirmDialog
        open={deleteConfirm.isOpen}
        onOpenChange={deleteConfirm.setIsOpen}
        onConfirm={deleteConfirm.handleConfirm}
        title="Delete Action Point?"
        description={`Are you sure you want to delete this action point${itemToDelete?.description
          ? `: "${itemToDelete.description.substring(0, 50)}${itemToDelete.description.length > 50 ? "..." : ""
          }"`
          : ""
          }? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        variant="destructive"
        icon="danger"
      />

      {/* Close Modal Confirmation */}
      <ConfirmDialog
        open={closeConfirm.isOpen}
        onOpenChange={closeConfirm.setIsOpen}
        onConfirm={closeConfirm.handleConfirm}
        title="Discard Changes?"
        description="You have unsaved action points. Are you sure you want to close without saving?"
        confirmText="Yes, Close"
        cancelText="Keep Editing"
        variant="warning"
        icon="warning"
      />
    </>
  );
};

export default MOMModal;
